import {
  MessageModel,
  MessageType,
  MessageMsgFrom,
  SocketServerMessage,
  MessageMsgType,
  MessagesSet,
  SocketChannel,
  SocketMessage,
  SocketClientEvent,
  generateUuid,
} from '@tencent/dz-models';
import { ImSocket } from '@tencent/dz-im';
import { ModuleParams } from '@tencent/dz-report';
import { ImpCoreModel } from './ImpCoreMode';
import { LeaveMsgTypeEnum, LeaveMsgTypeWithMsgTypeMap, InputType, MessageSourceFrom, HeartBeatType, ChatActivate } from '../const';
import { SocketResponse, SendMsgPipeHook } from '../interface';
import { getMessageSource } from '../utils';
import { config } from 'src/config';
import Cookies from 'js-cookie';
import { logger, report } from 'src/helper';


interface FormOptionType {
  source: ModuleParams,
  title?: string,
}

interface LeaveOptionType {
  title?: string,
}
export class ImpMessageModel extends MessageModel {
  coreModel: ImpCoreModel;
  // 是否已经留言
  public leaveMessageSendTag = false;
  // 系统是否给客户发送过离线消息
  public leaveMessageShowed = false;
  // 是否收到客服消息
  public msgRecv = false;
  /**
   * 当前是否发送了form消息，发送时，需要置为true
   */
  public lastMessageIsForm = false;
  // 机器人
  public isRobot = false;
  public answerRecv = 0;
  public currentQuestionIndex = 0;
  public robotQuestions: any[] = [];
  public robotTimer!: NodeJS.Timeout;
  public robotWaitingTime: number;
  // socket 实例，这里可以监测是否连接
  // TODO: 这里使用了mock模式，上线之前需要改成 public socket = new ImSocket<SocketMessage>();
  public socket = new ImSocket<SocketMessage>(false);
  // 原项目用这个字段判断是否im联通，不知道为什么
  // 应该是表示客服接入 或者 机器人接入聊天了
  public imOpen = false;
  // 和客服建立过对话
  public chatInfo = {
    chat_id: '',
    chat_sign: '',
    guest_id: '\'',
    status: 0,
  };
  // ws建立连接时刻，用于计算至发送start消息的时间间隔
  public connectTime = 0;

  public uuid = generateUuid();

  // 定时器指针
  public socketTimer!: NodeJS.Timeout;
  // 消息渠道： IM、robot
  public channel: SocketChannel;

  // 正在输入中定时器
  public inputTimer!: NodeJS.Timeout;

  constructor(coreModel: ImpCoreModel) {
    super();
    this.coreModel = coreModel;
    const consultModel = coreModel.getConsultModel();
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {
      is_robot_start,
      robot_questions,
      robot_auto_im_start_time,
    } = consultModel.getConsultConfig();
    this.isRobot = !!is_robot_start;
    this.channel = is_robot_start ? SocketChannel.Robot : SocketChannel.Im;
    this.robotQuestions = robot_questions;
    this.robotWaitingTime = (robot_auto_im_start_time || 10) * 1000;
  }

  // 进线连接, 根据规则判断
  public connectRule() {
    const consultModel = this.coreModel.getConsultModel();
    const leaveModel = this.coreModel.getLeaveModel();

    const connectType = consultModel.getContactTimingType();

    report.exposure({
      module_type: 'session_connection',
      module_chinese_type: '会话连接',
      module_value: '',
      module_id: '',
      element_type: 'start',
      element_chinese_type: '准备连接',
      element_id: connectType,
    });

    if (consultModel.isHalfConsult() && connectType === 1 && this.isRobot) {
      this.connect();
    } else if (connectType === 1 || this.isRobot || leaveModel.hasHisotryMessage) {
      this.connect();
    } else if (connectType === 3) {
      this.socketTimer = setTimeout(() => {
        this.connectMonitor();
      }, 5000);
    }
  }

  // 连接检测
  public connectMonitor() {
    if (!this.socket.isConnected()) {
      logger.info('[ImpMessageModel]', 'im ReConnection');
      this.connect();
    }
  }

  public connect() {
    logger.info('[ImpMessageModel]', 'im connection');
    const consultModel = this.coreModel.getConsultModel();
    if (consultModel.unConnectIm()) return;
    this.socket.connect(config.ws.url, () => {}, {
      path: '/consult-socket.io',
      timeout: 5000,
      requestTimeout: 5000,
      query: {
        traceid: this.uuid,
      },
    });

    this.connectTime = new Date().getTime();

    logger.report(`connect: traceid: ${this.uuid} connectTime: ${this.connectTime}, clickId: ${consultModel.clickID}`);

    // 机器人会话建立
    this.socket.on(SocketServerMessage.Robot_Im_Start_Chat_Info, this.socketMessageOfRobotChatInfo);
    // 机器人会话
    this.socket.on(SocketServerMessage.Robot_Start_Chat_Info, this.socketMessageOfRobotStartChatInfo);
    this.socket.on(SocketServerMessage.Robot_Send_Msg_Res, this.socketMessageOfRobotSendMsgRes);

    // 客服会话建立
    this.socket.on(SocketServerMessage.Chat_Info, this.socketMessageOfChatInfo);
    //
    this.socket.on(SocketServerMessage.Server_Msg, this.socketMessageOfServerMsg);
    this.socket.on(SocketServerMessage.Chat_End, this.socketMessageOfChatEnd);
    this.socket.on(SocketServerMessage.Msg_Recv_Suc, this.socketMessageOfMsgRecvSuc);
    this.socket.on(SocketServerMessage.Msg_Recv_Error, this.socketMessageOfMsgRecvError);
    this.socket.on(SocketServerMessage.Disconnect, this.socketMessageOfDisconnect);
    this.socket.on(SocketServerMessage.Connect, this.socketMessageOfConnect);
    this.socket.on(SocketServerMessage.Connect_Timeout, this.socketMessageOfTimeout);
    this.socket.on(SocketServerMessage.Connect_Error, this.socketMessageOfError);
    this.socket.on(SocketServerMessage.Reconnect_Attempt, this.socketMessageOfAttempt);
    return this.socket;
  }

  public socketMessageOfAttempt = () => {
    /**
     * 连接超时或者错误重试的时候使用长轮训
     */
    logger.info('[ImpMessageModel]', 'reconnect_attempt');
    const consultModel = this.coreModel.getConsultModel();

    this.connectTime = new Date().getTime();
    logger.report(`reconnect_attempt: traceid: ${this.uuid} clickId: ${consultModel.clickID} connectTime: ${this.connectTime}`);

    report.exposure({
      module_type: 'session_connection',
      module_chinese_type: '会话连接',
      module_value: '',
      module_id: '',
      element_type: 'retry',
      element_chinese_type: '重试',
      element_id: this.chatInfo?.chat_id,
    });
  };

  public socketMessageOfError = (error: any) => {
    logger.info('[ImpMessageModel]', 'Connect_Error', error);
    const consultModel = this.coreModel.getConsultModel();
    // TAM上报
    logger.report(`Connect_Error: click_id: ${consultModel.clickID} traceid:${this.uuid} error: ${error}`);
    report.exposure({
      module_type: 'session_connection',
      module_chinese_type: '会话连接',
      module_value: '',
      module_id: '',
      element_type: 'error',
      element_chinese_type: '错误',
      element_id: this.chatInfo?.chat_id,
    });
  };

  public socketMessageOfTimeout = () => {
    logger.info('[ImpMessageModel]', 'Connect_Timeout');
  };

  public connectAfterHandler(event: SocketClientEvent, channel: SocketChannel) {
    const consultModel = this.coreModel.getConsultModel();
    const config = consultModel.getConsultConfig();

    logger.info('[ImpMessageModel]', `im Connection start channel:${SocketChannel[channel]}`);

    // 计算时间间隔
    const duration = new Date().getTime() - this.connectTime;

    logger.report(`sendstart: duration: ${duration},clickId: ${consultModel.clickID}, traceid: ${this.uuid}, currentTime: ${new Date().getTime()}`);

    report.exposure({
      module_type: 'session_connection',
      module_chinese_type: '会话连接',
      module_value: String(duration),
      module_id: '',
      element_type: 'establish',
      element_chinese_type: '建立连接',
      element_id: this.chatInfo?.chat_id,
    });

    if (event === SocketClientEvent.Robot_Im_Start) {
      this.socket.send(event, {
        u_id: consultModel.uid,
        consult_id: config.consult_id,
        chat_id: this.chatInfo.chat_id,
        pv_id: window.pvid,
      });
    } else {
      this.socket.send(event, {
        u_id: consultModel.uid,
        consult_id: config.consult_id,
        guest_id: consultModel.guestID,
        gdt_uniq_user_id: consultModel.gdtUID,
        click_id: consultModel.clickID,
        trace_id: consultModel.traceID,
        h5url: window.location.href,
        landing_page_url: consultModel.landingUrl,
        pv_id: window.pvid,
      });
    }

    this.channel = channel;
  }

  public socketMessageOfConnect = () => {
    logger.info('[ImpMessageModel]', 'Connection start of websocket');

    if (this.isRobot && this.channel === SocketChannel.Robot) {
      this.connectAfterHandler(SocketClientEvent.Robot_Start, SocketChannel.Robot);
    } else {
      this.connectAfterHandler(SocketClientEvent.Start, SocketChannel.Im);
    }
  };

  public socketMessageOfRobotChatInfo = (res: SocketResponse) => {
    logger.info('[ImpMessageModel]', 'RobotChatInfo', res);

    this.imOpen = true;
    const { code, data } = res;
    if (code !== 0) {
      return this.pushLeaveMessage();
    }

    report.exposure({
      module_type: 'session_connection',
      module_chinese_type: '会话连接',
      module_value: 'robot',
      module_id: '',
      element_type: 'success',
      element_chinese_type: '连接成功',
      element_id: this.chatInfo?.chat_id,
    });

    this.messageSynchronization(data?.status);
  };

  public socketMessageOfRobotStartChatInfo = (res: SocketResponse) => {
    logger.info('[ImpMessageModel]', 'RobotStartChatInfo', res);

    if (res.code !== 0) {
      return this.pushLeaveMessage();
    }

    this.chatInfo = res.data;

    this.updateLeaveMessageTag();

    this.sendRobotQuestion();
  };

  public socketMessageOfRobotSendMsgRes = (res: SocketResponse) => {
    logger.info('[ImpMessageModel]', 'RobotSendMsgRes', res);

    if (res.code !== 0) return;

    const consultModel = this.coreModel.getConsultModel();

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { is_cursed, from, is_first_answer } = res.data;

    if (is_cursed) {
      this.socket.close();
      // 设置为骚扰客户
      consultModel.setIsCursed(true);
    }

    if (from === 0 && this.currentQuestionIndex - 1 === this.answerRecv && is_first_answer) {
      this.answerRecv = this.answerRecv + 1;
      if (this.answerRecv < this.robotQuestions.length && !consultModel.isCursed) {
        setTimeout(() => {
          this.sendRobotQuestion();
        }, 300);
      } else if (this.answerRecv === this.robotQuestions.length && !consultModel.isCursed) {
        const config = this.coreModel.getConsultModel().getConsultConfig();
        this.pushLeaveMessage({ title: config?.guide_msg });
      }
    }

    const isNotDuplicate = this.channel === SocketChannel.Im || is_first_answer;
    if (this.answerRecv >= this.robotQuestions.length && !consultModel.isCursed && from === 0 && isNotDuplicate) {
      this.connectAfterHandler(SocketClientEvent.Robot_Im_Start, SocketChannel.Im);
    }
  };

  public socketMessageOfChatInfo = async (res: SocketResponse) => {
    logger.info('[ImpMessageModel]', 'Chat_Info', res);
    this.imOpen = true;

    if (res.code !== 0) {
      return this.pushLeaveMessage();
    }

    const chatInfo = res.data;
    this.chatInfo = chatInfo;

    // 这里需要监控逻辑
    report.paramsReport({ chat_id: chatInfo?.chat_id });
    // 会话激活状态 1-激活；2-未激活, 默认未激活
    window.chatActivate = ChatActivate.active;

    report.heart({
      heart_beat_type: HeartBeatType.chat_info,
    });

    report.exposure({
      module_type: 'session_connection',
      module_chinese_type: '会话连接',
      module_value: '',
      module_id: '',
      element_type: 'success',
      element_chinese_type: '连接成功',
      element_id: this.chatInfo?.chat_id,
    });

    this.messageSynchronization(chatInfo?.status);

    this.updateLeaveMessageTag();
  };

  public socketMessageOfMsgRecvSuc = () => {
    logger.info('[ImpMessageModel]', 'Msg_Recv_Suc');
    // 在msg_recv_suc收到消息最近的一条时，显示留言成功
    if (this.lastMessageIsForm) {
      this.pushMessage({
        type: MessageType.SystemCustom,
        msg_from: MessageMsgFrom.Server,
        payload: { message: '留言成功' },
      });
      this.lastMessageIsForm = false;
    }
  };

  public socketMessageOfMsgRecvError = (resonse: SocketResponse) => {
    logger.info('[ImpMessageModel]', 'Msg_Recv_Error', resonse);
    const { code } = resonse;
    if (code === 30002) {
      this.updateLeaveMessageTag({ sended: false });
      this.pushMessage({
        type: MessageType.SystemCustom,
        msg_from: MessageMsgFrom.Server,
        payload: { message: '提交失败，为尽快与您联系，请重新填写' },
      });
    }
  };

  public socketMessageOfDisconnect = () => {
    // 连接断开
    logger.info('[ImpMessageModel]', 'Disconnect');
    this.imCloseHandler();
  };

  public socketMessageOfServerMsg = (response: SocketResponse) => {
    logger.info('[ImpMessageModel]', 'Server_Msg', response);
    this.msgRecv = true;
    // 客服在线
    this.updateServiceOnline(true);

    const consultModel = this.coreModel.getConsultModel();
    const { mobile } = this.coreModel.getMobileModel();

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { msg_from, msg_type = 0, recommend, msg_content } = response;

    // 有的渠道过来的类型不是number
    const msgType = parseInt((msg_type).toString(), 10);

    // 1. 输入中信号
    if (this.handleInputting(msg_from, msgType)) return;

    // 2. 客服消息推送到屏
    this.pushMessage({
      type: MessageType.Server,
      msg_type: msgType,
      msg_from: msg_from || MessageMsgFrom.Server,
      payload: {
        recommend,
        message: msg_content!,
        tell: recommend?.showSendPhoneNumberCard ? mobile : '',
        tell_validate: true,
      },
    });

    // 搜索场景,有的消息需要同时推送一键发送手机号
    if (consultModel.isSearchConsult() && recommend?.showSendPhoneNumberCard) {
      this.pushMessage({
        type: MessageType.QuickPhoneForm,
        msg_from: MessageMsgFrom.Server,
        msg_content,
        payload: {
          tell: mobile || '',
          tell_validate: true,
        },
      });
    }
  };

  public handleInputting(msgFrom: number | undefined, msgType: number | undefined) {
    if (msgFrom === MessageMsgFrom.ServerSystem && msgType === MessageMsgType.System) {
      // 客服输入中
      this.setInputing(true);
      this.inputTimer && clearTimeout(this.inputTimer);
      this.inputTimer = setTimeout(() => {
        this.setInputing(false);
      }, 10000);
      return true;
    }
    if (msgFrom === MessageMsgFrom.Server) {
      // 收到消息，隐藏输入中
      this.setInputing(false);
      this.inputTimer && clearTimeout(this.inputTimer);
    }
    return false;
  }

  public socketMessageOfChatEnd = (response: SocketResponse) => {
    logger.info('[ImpMessageModel]', 'Chat_End', response);
    this.imCloseHandler();
  };

  public imCloseHandler() {
    // 只执行一次
    if (this.imOpen) {
      logger.info('[ImpMessageModel]', 'run imCloseHandler job');
      this.imOpen = false;
      this.chatInfo.chat_id = '';
      // 关闭已读
      this.updateServiceOnline(false);
      // 2. 关闭socke逻辑及重新变量状态
      this.socket.close();

      window.chatActivate = ChatActivate.un_active;
      report.heart({
        heart_beat_type: HeartBeatType.chat_end,
      });
    }
  }
  // 客服离线发送消息
  public pushLeaveMessage(option?: LeaveOptionType) {
    if (this.leaveMessageShowed || this.leaveMessageSendTag) return;
    // 离线消息发送过就不在此发送；
    const consultModel = this.coreModel.getConsultModel();

    const config = consultModel.getConsultConfig();

    this.leaveMessageShowed = true;

    if (config.leave_msg_enable) {
      const { mobile } = this.coreModel.getMobileModel();

      const messageModel = this.coreModel.getMessageModel();
      const isSearchConsult = consultModel.isSearchConsult();

      if (isSearchConsult) {
        this.updateServiceOnline(false);
        messageModel.pushMessage([
          {
            type: MessageType.SystemCustom,
            msg_from: MessageMsgFrom.Server,
            payload: {
              message: '客服正忙，请留下您的联系方式，客服会尽快联系您',
            },
          },
          {
            type: MessageType.QuickPhoneForm,
            msg_from: MessageMsgFrom.Server,
            payload: {
              tell: mobile,
              tell_validate: true,
              is_offline: true,
            },
          },
        ]);
        return;
      }

      const reportModuleParams = {
        module_id: '',
        module_value: '离线消息',
        module_type: 'message',
        module_chinese_type: '消息',
      };
      messageModel.pushMessage({
        type: MessageType.OfflineForm,
        msg_from: MessageMsgFrom.Server,
        payload: {
          reportModuleParams,
          leaveMsgType: LeaveMsgTypeWithMsgTypeMap[MessageType.OfflineForm],
          title: option?.title || '',
        },
      });
    }
  }

  // 消息互相同步： 上屏客服连接状态消息，同步客户离线消息
  public messageSynchronization(status: number) {
    this.msgRecv = false;

    if (status === 0) {
      this.updateServiceOnline(true);
    }

    if (status === 0 || status === 2) {
      const message = status === 0 ? '人工客服已接入' : '客服连线中';

      this.pushMessage({
        type: MessageType.SystemCustom,
        msg_from: MessageMsgFrom.Server,
        payload: {
          message,
        },
      });

      this.sendOfflineMessage();
      setTimeout(() => {
        if (!this.msgRecv && !this.isInRobotQuestionStage()) {
          this.pushLeaveMessage();
        }
      }, 10000);
      return;
    }

    this.sendOfflineMessage();

    if (this.isInRobotQuestionStage()) return;

    // 如果智能客服二期有表单，则十秒钟后再弹出离线表单
    if (this.robotQuestions?.[0].answers?.length) {
      setTimeout(() => {
        this.pushLeaveMessage();
      }, 10000);
    } else {
      this.pushLeaveMessage();
    }
  }

  public sendRobotQuestion() {
    if (
      !(
        this.isRobot
        && this.robotQuestions.length > this.currentQuestionIndex
        && this.currentQuestionIndex === this.answerRecv
      )
    ) {
      return;
    }

    const leaveModel = this.coreModel.getLeaveModel();
    const consultModel = this.coreModel.getConsultModel();
    const { question } = this.robotQuestions[this.currentQuestionIndex];

    this.pushMessage({
      type: MessageType.Server,
      msg_type: MessageMsgType.Rich,
      msg_from: MessageMsgFrom.Server,
      payload: {
        message: question,
        answers: this.robotQuestions[this.currentQuestionIndex].answers,
      },
    });

    this.socket.send(SocketClientEvent.Robot_Answer_Msg_Send, {
      u_id: consultModel.uid,
      chat_id: this.chatInfo.chat_id,
      chat_sign: this.chatInfo.chat_sign,
      from: MessageMsgFrom.FmcRobot,
      msg_content: JSON.stringify(this.robotQuestions[this.currentQuestionIndex]),
      msg_type: MessageMsgType.Rich,
      question_seq: this.currentQuestionIndex,
    });

    this.currentQuestionIndex = this.currentQuestionIndex + 1;

    this.robotTimer = setTimeout(() => {
      // 用户是否在打字, 搜索场景不推荐
      if (!this.isTyping) {
        leaveModel.fetchRecommendation();
      }
      // 展示推荐问题，则设置机器人接待阶段已结束
      this.currentQuestionIndex = this.robotQuestions.length;
      this.answerRecv = this.robotQuestions.length;
      this.robotTimer = setTimeout(() => {
        this.connectAfterHandler(SocketClientEvent.Robot_Im_Start, SocketChannel.Im);
      }, this.robotWaitingTime);
    }, this.robotWaitingTime);
  }

  // 客户发送消息
  public sendMessage(message: string, option: any = {}) {
    this.lastMessageIsForm = false;
    const { msgType, inputType } = option;

    const messageType = msgType ||  MessageMsgType.Text;
    const currentMessage: MessagesSet = {
      type: MessageType.Customer,
      msg_type: messageType,
      msg_from: MessageMsgFrom.Server,
      msg_read_status: false,
      msg_is_new: true,
      payload: { message },
    };
    logger.info('[ImpMessageModel]', 'SendMessage', currentMessage);

    this.pushMessage(currentMessage);

    const consultModel = this.coreModel.getConsultModel();

    if (consultModel.isSearchConsult()) {
      const headerModel = this.coreModel.getHeaderModel();
      headerModel.updateIsShowTopCard(false);
    }

    if (consultModel.isCursed) return;

    const msgSource = getMessageSource(message, this.inputMessageSource);

    this.sendMsgPipe({
      pushOfflineMessageHook: () => {
        this.pushOfflineMessage({
          msg_type: messageType,
          from: MessageMsgFrom.Customer,
          msg_content: message,
          ...msgSource,
        });
      },
      sendMessageToServerHook: () => {
        this.socket.send(SocketClientEvent.Msg_Send, {
          chat_id: this.chatInfo.chat_id,
          chat_sign: this.chatInfo.chat_sign,
          msg_content: message,
          from: MessageMsgFrom.Customer,
          msg_type: messageType,
          ...msgSource,
        });
      },
      SendMessageToRobotHook: () => {
        clearTimeout(this.robotTimer);
        const consultModel = this.coreModel.getConsultModel();
        this.socket.send(SocketClientEvent.Robot_Answer_Msg_Send, {
          u_id: consultModel.uid,
          chat_id: this.chatInfo.chat_id,
          chat_sign: this.chatInfo.chat_sign,
          msg_content: message,
          from: MessageMsgFrom.Customer,
          input_type: inputType === 'click' ? 1 : 0,
          msg_type: messageType,
        });
      },
    });
  }

  async getImageUrl(url: string, file: any) {
    const defaultHeader = {
      'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
    };
    const response = await this.coreModel.request.post(url, file, { headers: defaultHeader });
    return response;
  }

  // 推出表单
  public pushFormMessage = (type: number, option: FormOptionType) => {
    if (this.leaveMessageSendTag) {
      this.pushMessage({
        type: MessageType.SystemCustom,
        msg_from: MessageMsgFrom.Server,
        payload: {
          message: '您已提交信息，我们会尽快与您联系。若您想重新留下联系方式，请在对话框中填写。',
        },
      });
      return;
    }
    this.pushMessage({
      type,
      msg_from: MessageMsgFrom.Server,
      payload: {
        title: option.title,
        reportModuleParams: option.source,
        leaveMsgType: LeaveMsgTypeWithMsgTypeMap[type] || LeaveMsgTypeEnum.QuestionForm,
      },
    });
  };


  // 提交表单
  public sendFormMessage = (formValue: any = {}) => {
    this.connectMonitor();
    if (this.leaveMessageSendTag) {
      this.pushMessage({
        type: MessageType.SystemCustom,
        msg_from: MessageMsgFrom.Server,
        payload: {
          message: '请勿重复留言',
        },
      });
      return;
    }

    const { phone, content, msgType, name } = formValue;

    const isQuickPhoneForm = msgType === MessageType.QuickPhoneForm;

    if (isQuickPhoneForm) {
      // 一键发送手机号复用表单提交方式，但是页面需要展示一条客户消息
      this.pushMessage({
        type: MessageType.Customer,
        msg_type: MessageMsgType.Text,
        msg_from: MessageMsgFrom.Server,
        payload: {
          message: `我的联系方式：${phone}`,
        },
      });
    }

    const consultModel = this.coreModel.getConsultModel();
    if (consultModel.isSearchConsult()) {
      const headerModel = this.coreModel.getHeaderModel();
      headerModel.updateIsShowTopCard(false);
    }

    const mobileModel = this.coreModel.getMobileModel();
    const leaveMsgType = LeaveMsgTypeWithMsgTypeMap[msgType] || LeaveMsgTypeEnum.QuestionForm;
    // im没连接，消息推到离线
    if (!this.chatInfo.chat_id) {
      this.pushOfflineMessage({
        msg_content: JSON.stringify({
          leave_msg_content: {
            customer_name: name,
            customer_phone: phone === mobileModel.mobile ? '' : phone,
            customer_msg: isQuickPhoneForm ? '' : content, // type === 15代表一键发送手机号，不需要传递customer_msg
            encrypt_phone: mobileModel.encryptPhone, // 获取的号码加密串，使用一键获取功能且用户未修改时填写
            carrier_type: mobileModel.carrierType, // 运营商类型编码  0-默认 1-移动  2-联通 3-电信
          },
          leave_msg_type: leaveMsgType,
        }),
        msg_type: msgType,
        msg_from: MessageMsgFrom.Server,
        input_type: InputType.active,
        msg_source_from: MessageSourceFrom.default,
      });
    } else {
      this.socket.send(SocketClientEvent.Msg_Send, {
        msg_content: JSON.stringify({
          leave_msg_content: {
            customer_name: name,
            customer_phone: phone === mobileModel.mobile ? '' : phone,
            customer_msg: isQuickPhoneForm ? '' : content, // type === 15代表一键发送手机号，不需要传递customer_msg
            encrypt_phone: mobileModel.encryptPhone, // 获取的号码加密串，使用一键获取功能且用户未修改时填写
            carrier_type: mobileModel.carrierType, // 运营商类型编码  0-默认 1-移动  2-联通 3-电信
          },
          leave_msg_type: leaveMsgType,
        }),
        msg_type: msgType,
        chat_id: this.chatInfo.chat_id,
        chat_sign: this.chatInfo.chat_sign,
        input_type: InputType.active,
        msg_source_from: MessageSourceFrom.default,
      });
    }

    this.lastMessageIsForm = true;
    this.updateLeaveMessageTag({ sended: true });
  };

  // 离线消息发送
  public sendOfflineMessage() {
    logger.info('[ImpMessageModel]', 'SendOfflineMessage', this.offlineMessageQueue);
    if (this.offlineMessageQueue.length) {
      this.offlineMessageQueue.forEach((message) => {
        this.socket.send(SocketClientEvent.Msg_Send, {
          chat_id: this.chatInfo.chat_id,
          chat_sign: this.chatInfo.chat_sign,
          msg_content: message.msg_content,
          from: message.from || 0,
          msg_type: message.msg_type,
          input_type: message.input_type, // 对于访客消息 0 非主动发送 1 主动发送
          msg_source_from: message.msg_source_from,
        });
      });
      this.offlineMessageQueue = [];
    }
  }

  public updateLeaveMessageTag(action?: { sended: boolean }) {
    // set
    if (action) {
      Cookies.set(`leaveMessageSend${this.chatInfo.chat_id}`, `${Number(action.sended)}`, {
        expires: 60 * 60 * 24 * 10,
      });
      this.leaveMessageSendTag = action.sended;
      return;
    }
    // get
    const val = Cookies.get(`leaveMessageSend${this.chatInfo.chat_id}`);
    this.leaveMessageSendTag = val ? !!val : this.leaveMessageSendTag;
  }

  public isInRobotQuestionStage() {
    const consultModel = this.coreModel.getConsultModel();
    if (this.robotQuestions && this.robotQuestions.length > this.currentQuestionIndex && !consultModel.isCursed) {
      return true;
    }
    return false;
  }

  // 发送系统客服消息
  public sendServerMessage(message: string, msgType: MessageMsgType) {
    this.pushMessage({
      type: MessageType.Server,
      msg_type: msgType,
      msg_from: MessageMsgFrom.Server,
      payload: {
        message,
      },
    });

    if (!this.imOpen) {
      this.pushOfflineMessage({
        msg_type: msgType,
        from: MessageMsgFrom.FmcSystem,
        msg_content: message,
        chat_id: this.chatInfo.chat_id,
        chat_sign: this.chatInfo.chat_sign,
      });
    } else {
      this.socket.send(SocketClientEvent.Msg_Send, {
        chat_id: this.chatInfo.chat_id,
        chat_sign: this.chatInfo.chat_sign,
        msg_content: message,
        from: MessageMsgFrom.FmcSystem,
        msg_type: msgType,
      });
    }
  }


  private sendMsgPipe({
    pushOfflineMessageHook,
    sendMessageToServerHook,
    SendMessageToRobotHook,
  }: SendMsgPipeHook) {
    this.connectMonitor();

    if (!this.chatInfo.chat_id) {
      pushOfflineMessageHook?.();
      return;
    }
    if (this.channel === SocketChannel.Im && this.imOpen) {
      sendMessageToServerHook?.();
    } else if (this.channel === SocketChannel.Im && !this.imOpen) {
      pushOfflineMessageHook?.();
    } else {
      SendMessageToRobotHook?.();
    }
  }
}
